// WHAT: Custom hook for loading and saving workspace configuration
// WHY:  Encapsulates config loading/saving logic with error handling and state management
// DOMAIN: Settings → TimeTracQ → Workspace Management
// DEPENDENCIES: Tauri core API, workspace commands
// RISKS:
//   - WARN: Workspace sorting order is hardcoded - may not match user expectations
//   - NOTE: Debug commands are called but results not used - may be unnecessary
//   - WARN: Workspaces not in sort order list are excluded from display

import type { AspectConfig, WorkspaceConfig } from "../types";

import { core } from "@tauri-apps/api";
import { useState, useCallback } from "react";

/**
 * WHAT:
 *   Custom hook for managing workspace configuration loading and saving.
 *
 * WHY:
 *   Provides centralized state management for configuration operations with proper error handling.
 *
 * RETURNS:
 *   Object containing config state, loading/saving states, error/success messages, and operations.
 *
 * @returns {object} Object with `config`, `isConfigured`, `loading`, `saving`, `error`, `success`, `loadConfig`, `saveConfig`, `loadWorkspaceNames`, `setConfig`, `setError`, `setSuccess`
 *
 */
export function useWorkspaceConfig() {
  const [config, setConfig] = useState<undefined | WorkspaceConfig>();
  const [isConfigured, setIsConfigured] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [success, setSuccess] = useState<string | undefined>();

  /**
   * WHAT:
   *   Loads workspace configuration from database and applies predefined sorting order.
   *
   * WHY:
   *   Retrieves configuration data and organizes workspaces in consistent order for better UX.
   *
   * SIDE EFFECTS:
   *   - Invokes debug commands for diagnostics
   *   - Fetches full configuration from database
   *   - Updates component state with organized configuration
   *
   * RISKS:
   *   - WARN: Workspace order is hardcoded - may not match user preferences
   *   - NOTE: Debug commands are called but results not used - may be unnecessary
   *   - WARN: Workspaces not in sort order list are excluded from display
   */
  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(undefined);

      await core.invoke("debug_database_content");
      await core.invoke("debug_workspace_loading");

      const rawData = await core.invoke<Record<string, unknown>>("get_json_config_cmd");

      const { settings, ...workspaces } = rawData;

      const workspaceOrder = [
        "Nexus",
        "Investments",
        "Trading",
        "ResponsumOS",
        "Real Estate",
        "TracQ",
      ];

      const sortedWorkspaces: Record<string, Record<string, AspectConfig>> = {};
      for (const workspaceName of workspaceOrder) {
        if (workspaces[workspaceName]) {
          sortedWorkspaces[workspaceName] = workspaces[workspaceName] as Record<
            string,
            AspectConfig
          >;
        }
      }

      const data: WorkspaceConfig = {
        settings: (settings && typeof settings === "object"
          ? (settings as WorkspaceConfig["settings"])
          : undefined) || {
          chunk_size: 1,
          daily_capacity_weekday: 5,
          daily_capacity_weekend: 8,
          max_chunk_per_task: 3,
          minimum_chunk_hours: 0.5,
        },
        workspaces: sortedWorkspaces,
      };

      setConfig(data);
      setIsConfigured(Object.keys(sortedWorkspaces).length > 0);
    } catch (error_) {
      const message =
        error_ instanceof Error ? error_.message : "Failed to load workspace configuration";
      setError(message);
      setIsConfigured(false);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * WHAT:
   *   Saves entire workspace configuration to Supabase database.
   *
   * WHY:
   *   Persists all workspace and aspect changes to database for persistence across sessions.
   *
   * SIDE EFFECTS:
   *   - Saves full configuration object to database
   *   - Updates success/error state
   *
   * RISKS:
   *   - WARN: Saves entire config object - may overwrite other configuration sections
   *   - NOTE: No validation of configuration structure before save
   *   - WARN: Unsaved changes are lost if save fails
   */
  const saveConfig = useCallback(async () => {
    if (!config) {
      return;
    }

    try {
      setSaving(true);
      setError(undefined);
      setSuccess(undefined);

      await core.invoke("save_json_config_cmd", { config });
      setSuccess("✅ Workspace configuration saved successfully!");
    } catch (error_) {
      const message = error_ instanceof Error ? error_.message : "❌ Error saving configuration";
      setError(message);
    } finally {
      setSaving(false);
    }
  }, [config]);

  /**
   * WHAT:
   *   Loads list of workspace names from database.
   *
   * WHY:
   *   Provides workspace names for dropdown selections in modals.
   *
   * RETURNS:
   *   Array of workspace name strings.
   */
  const loadWorkspaceNames = useCallback(async (): Promise<string[]> => {
    try {
      const names = await core.invoke<string[]>("get_workspace_names_cmd");
      return names;
    } catch {
      return [];
    }
  }, []);

  return {
    config,
    error,
    isConfigured,
    loadConfig,
    loading,
    loadWorkspaceNames,
    saveConfig,
    saving,
    setConfig,
    setError,
    setSaving,
    setSuccess,
    success,
  };
}
